/**
 * @module index
 * @package @geenius/adapters/solidjs-css
 * @description Defines the SolidJS CSS adapter primitives for reading adapter
 * instances and adapter status state from the SolidJS CSS provider context.
 * These primitives keep the SolidJS CSS runtime self-contained and on the
 * public contract.
 */

import type {
  AdapterDomain,
  AdapterStatusInfo,
  AdminAdapter,
  AiAdapter,
  AuthAdapter,
  DbAdapter,
  FileStorageAdapter,
  LoggerAdapter,
  PaymentsAdapter,
} from "@geenius/adapters";
import { createMemo } from "solid-js";
import { useAdapterContext } from "../provider";

/**
 * Reads the configured database adapter from the current SolidJS CSS adapter context.
 *
 * @returns The active database adapter implementation for the provider tree.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
export function createDb(): DbAdapter {
  const context = useAdapterContext();
  return context.getAdapter("db");
}

/**
 * Reads the configured authentication adapter from the current SolidJS CSS context.
 *
 * @returns The active authentication adapter implementation.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
export function createAuth(): AuthAdapter {
  const context = useAdapterContext();
  return context.getAdapter("auth");
}

/**
 * Reads the configured authentication adapter from the current SolidJS CSS context.
 * Alias for {@link createAuth} matching the React `useAuthAdapter` convention.
 *
 * @returns The active authentication adapter implementation.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
export function createAuthAdapter(): AuthAdapter {
  return createAuth();
}

/**
 * Reads the configured payments adapter from the current SolidJS CSS context.
 *
 * @returns The active payments adapter implementation.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
export function createPayments(): PaymentsAdapter {
  const context = useAdapterContext();
  return context.getAdapter("payments");
}

/**
 * Reads the configured AI adapter from the current SolidJS CSS context.
 *
 * @returns The active AI adapter implementation.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
export function createAi(): AiAdapter {
  const context = useAdapterContext();
  return context.getAdapter("ai");
}

/**
 * Reads the configured file-storage adapter from the current SolidJS CSS context.
 *
 * @returns The active file-storage adapter implementation.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
export function createStorage(): FileStorageAdapter {
  const context = useAdapterContext();
  return context.getAdapter("storage");
}

/**
 * Reads the configured file-storage adapter using the explicit file-storage primitive name.
 *
 * @returns The active file-storage adapter implementation.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
export function createFileStorage(): FileStorageAdapter {
  return createStorage();
}

/**
 * Reads the configured admin adapter from the current SolidJS CSS context.
 *
 * @returns The active admin adapter implementation.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
export function createAdmin(): AdminAdapter {
  const context = useAdapterContext();
  return context.getAdapter("admin");
}

/**
 * Reads the configured logger adapter from the current SolidJS CSS context.
 *
 * @returns The active logger adapter implementation.
 * @throws {AdapterContextError} When the provider or adapter is unavailable.
 */
export function createLogger(): LoggerAdapter {
  const context = useAdapterContext();
  return context.getAdapter("logger");
}

/**
 * Returns the full adapter status record from the current SolidJS CSS context.
 *
 * @returns A status object keyed by adapter domain.
 */
export function createAdapterStatuses(): Record<
  AdapterDomain,
  AdapterStatusInfo
> {
  const context = useAdapterContext();
  return context.statuses;
}

/**
 * Creates a memoized reader for a single adapter domain status.
 *
 * @param domain Adapter domain whose status should be observed.
 * @returns A getter that always reflects the latest status for that domain.
 */
export function createAdapterStatus(
  domain: AdapterDomain,
): () => AdapterStatusInfo {
  const context = useAdapterContext();
  return createMemo(() => context.statuses[domain]);
}

/**
 * Creates a memoized readiness reader for a single adapter domain.
 *
 * @param domain Adapter domain to inspect.
 * @returns A getter that reports whether the domain is ready for use.
 */
export function createIsAdapterReady(domain: AdapterDomain): () => boolean {
  const context = useAdapterContext();
  return createMemo(() => context.isReady(domain));
}

/**
 * Accesses every adapter and status helper from the current provider context.
 *
 * @returns A stable object exposing adapters, statuses, and loading helpers.
 */
export function createAdapters(): {
  db: DbAdapter | undefined;
  auth: AuthAdapter | undefined;
  payments: PaymentsAdapter | undefined;
  ai: AiAdapter | undefined;
  storage: FileStorageAdapter | undefined;
  fileStorage: FileStorageAdapter | undefined;
  admin: AdminAdapter | undefined;
  logger: LoggerAdapter | undefined;
  statuses: Record<AdapterDomain, AdapterStatusInfo>;
  isLoading: boolean;
  isReady: (domain: AdapterDomain) => boolean;
} {
  const context = useAdapterContext();

  return {
    db: context.adapters.db,
    auth: context.adapters.auth,
    payments: context.adapters.payments,
    ai: context.adapters.ai,
    storage: context.adapters.storage,
    fileStorage: context.adapters.fileStorage ?? context.adapters.storage,
    admin: context.adapters.admin,
    logger: context.adapters.logger,
    statuses: context.statuses,
    get isLoading() {
      return context.isLoading;
    },
    isReady: context.isReady,
  };
}
